import type { IntegrationStatus } from '@harness/shared/protocol';
import { useEffect, useRef, useState } from 'react';
import { connectUrl } from '../api';
import styles from '../styles/integrations.module.css';
import { integrationText, TEXT } from '../texts';

// Month as a name ("31 Oct 2026"), unambiguous for every reader.
const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

interface IntegrationsProps {
  projectId: string;
  /** null while loading. */
  integrations: IntegrationStatus[] | null | 'failed';
  onDisconnect: (id: string) => Promise<boolean>;
}

/** The consultant's own connections; connecting runs in a new tab, the worker reports the result over the socket. */
export function Integrations({ projectId, integrations, onDisconnect }: IntegrationsProps): React.JSX.Element {
  const [confirm, setConfirm] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const previous = useRef<IntegrationStatus[] | null>(null);

  // A login finishes in the other tab; the new status arrives here, so announce it.
  useEffect(() => {
    if (!Array.isArray(integrations)) return;
    const before = previous.current;
    previous.current = integrations;
    const added = before === null ? undefined : integrations.find((item) => item.connected && before.some((old) => old.id === item.id && !old.connected));
    if (added !== undefined) setNotice(TEXT.connectedNotice(integrationText(added.id).name));
  }, [integrations]);

  if (integrations === null) return <main className={styles.emptyPage}>{TEXT.loading}</main>;
  if (integrations === 'failed') return <main className={styles.emptyPage}>{TEXT.loadFailed}</main>;

  const disconnect = async (id: string): Promise<void> => {
    setConfirm(null);
    const name = integrationText(id).name;
    setNotice((await onDisconnect(id).catch(() => false)) ? TEXT.disconnectedNotice(name) : TEXT.disconnectFailed(name));
  };

  return (
    <main className={styles.main}>
      {notice !== null && (
        <div className={styles.notice} role="status">
          <span>{notice}</span>
          <button type="button" className={styles.close} aria-label={TEXT.closeNotice} onClick={() => setNotice(null)}>
            ×
          </button>
        </div>
      )}
      {integrations.length === 0 ? (
        <p className={styles.empty}>{TEXT.noIntegrations}</p>
      ) : (
        <section className={styles.grid}>
          {integrations.map((item) => (
            <Card key={item.id} projectId={projectId} item={item} onDisconnect={() => setConfirm(item.id)} />
          ))}
        </section>
      )}
      {confirm !== null && <ConfirmDisconnect id={confirm} onCancel={() => setConfirm(null)} onConfirm={() => void disconnect(confirm)} />}
    </main>
  );
}

function Card({ projectId, item, onDisconnect }: { projectId: string; item: IntegrationStatus; onDisconnect: () => void }): React.JSX.Element {
  const text = integrationText(item.id);
  const since = item.connectedAt === null ? TEXT.connectedUnknownDate : TEXT.connectedSince(DATE.format(item.connectedAt));
  return (
    <article className={styles.card} aria-label={text.name}>
      <div className={styles.head}>
        <span className={styles.tile} aria-hidden="true">
          {text.name.charAt(0).toUpperCase()}
        </span>
        <div>
          <h2 className={styles.name}>{text.name}</h2>
          <span className={item.connected ? styles.on : styles.off}>{item.connected ? TEXT.integrationConnected : TEXT.integrationNotConnected}</span>
        </div>
      </div>
      <p className={styles.description}>{text.description}</p>
      <dl className={styles.facts}>
        <dt>{TEXT.integrationAccess}</dt>
        <dd>{text.access}</dd>
        <dt>{TEXT.integrationSince}</dt>
        <dd>{item.connected ? since : '–'}</dd>
      </dl>
      <div className={styles.actions}>
        {item.connected ? (
          <button type="button" className={styles.secondary} onClick={onDisconnect}>
            {TEXT.disconnect}
          </button>
        ) : (
          <a className={styles.connect} href={connectUrl(projectId, item.id)} target="_blank" rel="noopener noreferrer">
            {TEXT.connect}
          </a>
        )}
      </div>
    </article>
  );
}

function ConfirmDisconnect({ id, onCancel, onConfirm }: { id: string; onCancel: () => void; onConfirm: () => void }): React.JSX.Element {
  const name = integrationText(id).name;
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancel.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="disconnect-title">
        <h2 id="disconnect-title" className={styles.dialogTitle}>
          {TEXT.disconnectTitle(name)}
        </h2>
        <p className={styles.description}>{TEXT.disconnectText(name)}</p>
        <div className={styles.dialogActions}>
          <button ref={cancel} type="button" className={styles.secondary} onClick={onCancel}>
            {TEXT.cancel}
          </button>
          <button type="button" className={styles.confirm} onClick={onConfirm}>
            {TEXT.disconnect}
          </button>
        </div>
      </div>
    </div>
  );
}
